import { defineStore } from "pinia";

import axios from "../../axios-auth";
import { pathOr } from "ramda";
import qs from "qs";
import { i18n } from "../../i18n";
import { useChangeOptionsStore } from "@/stores/modules/changeOptions";
import { useMyAccountStore } from "@/stores/modules/myAccount";
import { useWebproductStore } from "@/stores/modules/webproduct";
import { useUrlsStore } from "@/stores/modules/urls";

const STATUS = {
  PENDING: "PENDING",
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
  REQUEST_ERROR: "REQUEST_ERROR",
  FINALIZED: "FINALIZED"
};
const INTERVAL = 5000; //5000000000; //5000;
export const usePreflightPitstopStore = defineStore("preflightpitstop", {
  state: () => ({
    files: {
      /*
      file.pdf : {
          name:file.pdf,
          hash:abcd
          statsus:STTAUS.FINALIZED
          response : {...},

      }
       */
    },
    timer: null,
    preflightInfo: {}
  }),

  getters: {
    getPreflightSendData(state) {
      if (Object.keys(state.preflightInfo) == 0) {
        return {};
      }
      return { preflight_values: state.preflightInfo };
    },
    getFiles(state) {
      return state.files;
    },
    getPreflightInfo(state) {
      return state.preflightInfo;
    },
    getPendingHashFiles(state) {
      var hashes = [];
      for (var file in state.files) {
        if (state.files[file].status == STATUS.PENDING) {
          if (!hashes.includes(state.files[file].hash)) {
            hashes.push(state.files[file].hash);
          }
        }
      }
      return { files: hashes.join(",") };
    },
    getHasPendingFiles(state) {
      for (var file in state.files) {
        if (state.files[file].status == STATUS.PENDING) {
          return true;
        }
      }
      return false;
    },
    getTimer(state) {
      return state.timer;
    },
    getPitstopFileStatusResponse(state) {
      return function (file) {
        if (!state.files[file]) {
          return null;
        }
        if (state.files[file].status == STATUS.PENDING) {
          return null;
        } else if (state.files[file].status == STATUS.SUCCESS) {
          // dont show any pitstop information
          return null;
        } else if (state.files[file].status == STATUS.REQUEST_ERROR) {
          return {
            status: STATUS.REQUEST_ERROR
          };
        }

        return state.files[file].response;
      };
    },
    getPitstopFileIsLoading(state) {
      return function (file) {
        if (!state.files[file]) {
          return false;
        }
        if (state.files[file].status == STATUS.PENDING) {
          return true;
        }
        return false;
      };
    }
  },

  actions: {
    addFile(payload) {
      var sameHashFile = null;
      for (var file in this.files) {
        if (this.files[file].hash == payload.hash) {
          sameHashFile = file;
          break;
        }
      }
      var newFile = {
        [payload.name]: {
          name: payload.name,
          hash: payload.hash,
          product_part: payload.product_part,
          status: sameHashFile ? this.files[file].status : STATUS.PENDING,
          response: sameHashFile ? this.files[file].response : {}
        }
      };

      this.files = { ...this.files, ...newFile };
    },
    deleteFile(payload) {
      if (this.files[payload]) {
        var files = { ...this.files };
        delete files[payload];
        this.files = files;
      }
    },
    setTimer(payload) {
      this.timer = payload;
    },
    setErrorCode() {
      for (var file in this.files) {
        if (this.files[file].status == STATUS.PENDING) {
          this.files[file] = {
            ...this.files[file],
            status: STATUS.REQUEST_ERROR
          };
        }
      }
    },
    updateFiles(payload) {
      var files = { ...this.files };
      for (var file in payload) {
        var stateFiles = [];
        for (var tmpFile in files) {
          if (file == files[tmpFile].hash) {
            stateFiles.push(tmpFile);
          }
        }

        if (stateFiles.length == 0) {
          continue;
        }

        for (var stateFile of stateFiles) {
          if (files[stateFile]) {
            if (payload[file].status == "ok" && payload[file].data) {
              files[stateFile] = {
                ...files[stateFile],
                status: STATUS.SUCCESS
              };
            } else if (
              payload[file].status == "pending" ||
              (payload[file].status == "ok" && payload[file].data == undefined)
            ) {
              files[stateFile] = {
                ...files[stateFile],
                status: STATUS.PENDING
              };
            } else {
              files[stateFile] = {
                ...files[stateFile],
                status: STATUS.ERROR,
                response: {
                  data: payload[file].data,
                  preflight: payload[file].preflight,
                  status: payload[file].status
                }
              };
            }
          }
        }
      }

      this.files = files;
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
      }
      this.timer = null;
    },
    setPreflightInfo(payload) {
      var preflightInfo = { ...this.preflightInfo };
      preflightInfo[payload.code] = payload.preflightInfo;
      this.preflightInfo = preflightInfo;
      if (
        payload.request_finalized &&
        this.files[payload.uploadedFile].response
      ) {
        this.files[payload.uploadedFile].response.status = STATUS.FINALIZED;
      }
    },
    setPreflightInfoUpdated(payload) {
      const preflight = {};
      for (const pp in payload) {
        preflight[pp] = {};
        for (const att in payload[pp]) {
          if (att.startsWith("preflight")) {
            preflight[pp][att] = payload[pp][att];
          }
        }
      }
      this.preflightInfo = preflight;
    },
    removePreflightInfoPP(payload) {
      var newState = { ...this.preflightInfo };
      if (newState[payload]) {
        delete newState[payload];
        this.preflightInfo = newState;
      }
    },
    pitstopAddFile(payload) {
      this.addFile(payload);
      if (!this.timer) {
        // run now
        this.runPitstop(payload);
        // and setInterval
        var timer = setInterval(() => {
          this.runPitstop(payload);
        }, INTERVAL);
        this.setTimer(timer);
      }
    },
    pitstopDeleteFile(payload) {
      this.deleteFile(payload);
      if (Object.keys(this.getFiles).length == 0) {
        this.stopTimer();
      }
    },
    runPitstop(payload) {
      const changeOptionsStore = useChangeOptionsStore();
      const myAccountStore = useMyAccountStore();
      const webproductStore = useWebproductStore();
      const urlsStore = useUrlsStore();
      const files = this.getFiles;
      const changeOptionsData = changeOptionsStore.getChangeOptionsData;

      if (Object.keys(files).length == 0) {
        this.stopTimer();
        return;
      }

      let data = this.getPendingHashFiles;
      for (let file in files) {
        if (!files.hasOwnProperty(file)) {
          continue;
        }
        if (files[file].status != STATUS.PENDING) {
          continue;
        }
        var pages = null;
        var format = null;
        const product_part = files[file].product_part;
        if (myAccountStore.getIsMyAccount) {
          var uploadedData = myAccountStore.getMyAccountSelection;
          pages = pathOr(
            null,
            ["PP_" + product_part, "pages", 0],
            uploadedData
          );
          format = pathOr(
            null,
            ["PP_" + product_part, "format", 0],
            uploadedData
          );
        } else {
          pages = pathOr(
            null,
            ["pp_opt", "PP_" + product_part, "pages", 0],
            changeOptionsData
          );
          format = pathOr(
            null,
            ["pp_opt", "PP_" + product_part, "format", 0],
            changeOptionsData
          );
        }
        data[files[file].hash] = { pages, format, product_part };
      }
      if (i18n.locale) {
        data["lang"] = i18n.locale;
      }
      data["isPackaging"] = webproductStore.getConfig.enable_packaging
        ? webproductStore.getConfig.enable_packaging
        : 0;

      axios.instance
        .post(urlsStore.getUrl(axios.checkPreflight), qs.stringify(data))
        .then(response => {
          //console.log(response.data);
          if (response.data.success && response.data.files) {
            this.updateFiles(response.data.files);

            if (!this.getHasPendingFiles) {
              this.stopTimer();
            }
          }
        })
        .catch(error => {
          this.setErrorCode();
          this.stopTimer();
          console.log(error);
        });
    }
  }
});
